import { useState } from "react";

import { TransportError } from "../api/errors";

// The `Idempotency-Key` of a write (proposal 3.2). The server fingerprints the whole body under a
// key, so a key is resent only for the same body after its answer was lost (a TransportError: no
// envelope arrived, so the write may have happened); any answer from the server, or any change of
// the body, ends the attempt, and the next write mints a new key. Every write that sends a key
// follows this rule through these functions.

/** A write whose answer was lost: its key and the body it carried. */
export interface LostAttempt {
  key: string;
  body: string;
}

/** The key for this body: the lost attempt's when the body is the same, else a new one. */
export function attemptKey(body: unknown, lost: LostAttempt | undefined): string {
  return lost?.body === JSON.stringify(body) ? lost.key : crypto.randomUUID();
}

/** What a failed write leaves to resend: its attempt, only when no answer arrived. */
export function lostAttempt(key: string, body: unknown, error: unknown): LostAttempt | undefined {
  return error instanceof TransportError ? { key, body: JSON.stringify(body) } : undefined;
}

/**
 * The rule for a form with one write in flight. A screen with several at once (the upload panel,
 * one per file) keeps a `LostAttempt` per write and calls `attemptKey` and `lostAttempt` itself.
 */
export function useIdempotencyKey() {
  const [lost, setLost] = useState<LostAttempt | undefined>(undefined);
  return {
    /** The key for this body: the lost attempt's, or a new one. */
    keyFor(body: unknown): string {
      setLost(undefined);
      return attemptKey(body, lost);
    },
    /** Call with the write's error: a lost answer keeps the key for a resend of the same body. */
    failed(key: string, body: unknown, error: unknown): void {
      setLost(lostAttempt(key, body, error));
    },
  };
}
